import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PageHeader from '../components/common/PageHeader';

// ─── JWT helpers ─────────────────────────────────────────────

function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  try {
    return decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
  } catch {
    return atob(base64);
  }
}

function base64UrlEncode(str: string): string {
  return btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

interface JwtParts {
  raw: string;
  headerRaw: string;
  payloadRaw: string;
  signatureRaw: string;
  header: Record<string, unknown> | null;
  payload: Record<string, unknown> | null;
  headerError: string | null;
  payloadError: string | null;
}

function decodeJwt(token: string): JwtParts | null {
  const trimmed = token.trim();
  const parts = trimmed.split('.');
  if (parts.length !== 3) return null;

  const [headerRaw, payloadRaw, signatureRaw] = parts;
  let header: Record<string, unknown> | null = null;
  let payload: Record<string, unknown> | null = null;
  let headerError: string | null = null;
  let payloadError: string | null = null;

  try {
    header = JSON.parse(base64UrlDecode(headerRaw));
  } catch {
    headerError = 'Ungültiger Header (kein valides JSON)';
  }

  try {
    payload = JSON.parse(base64UrlDecode(payloadRaw));
  } catch {
    payloadError = 'Ungültiger Payload (kein valides JSON)';
  }

  return { raw: trimmed, headerRaw, payloadRaw, signatureRaw, header, payload, headerError, payloadError };
}

function rebuildToken(headerJson: string, payloadJson: string, signature: string): string {
  return `${base64UrlEncode(headerJson)}.${base64UrlEncode(payloadJson)}.${signature}`;
}

function formatTimestamp(val: unknown): string | null {
  if (typeof val !== 'number') return null;
  if (val < 1_000_000_000 || val > 10_000_000_000) return null;
  const d = new Date(val * 1000);
  return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'medium' });
}

function isExpired(payload: Record<string, unknown> | null): boolean {
  if (!payload || typeof payload.exp !== 'number') return false;
  return payload.exp * 1000 < Date.now();
}

// ─── Known claim descriptions ────────────────────────────────

const CLAIM_INFO: Record<string, string> = {
  iss: 'Issuer',
  sub: 'Subject',
  aud: 'Audience',
  exp: 'Expiration Time',
  nbf: 'Not Before',
  iat: 'Issued At',
  jti: 'JWT ID',
  name: 'Full Name',
  email: 'E-Mail',
  role: 'Role',
  roles: 'Roles',
  scope: 'Scope',
  azp: 'Authorized Party',
  typ: 'Token Type',
  sid: 'Session ID',
  nonce: 'Nonce',
  at_hash: 'Access Token Hash',
};

// ─── Component ───────────────────────────────────────────────

const SAMPLE_JWT =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';

const WorkbenchJwtPage: React.FC = () => {
  const theme = useTheme();
  const [token, setToken] = useState(SAMPLE_JWT);
  const [headerText, setHeaderText] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [decoded, setDecoded] = useState<JwtParts | null>(null);
  const [copied, setCopied] = useState(false);

  const decode = useCallback((raw: string) => {
    const result = decodeJwt(raw);
    setDecoded(result);
    if (result) {
      if (result.header) setHeaderText(JSON.stringify(result.header, null, 2));
      if (result.payload) setPayloadText(JSON.stringify(result.payload, null, 2));
    }
  }, []);

  useEffect(() => {
    decode(token);
  }, [token, decode]);

  const handleHeaderChange = (val: string) => {
    setHeaderText(val);
    if (decoded) {
      try {
        const newToken = rebuildToken(val, payloadText, decoded.signatureRaw);
        setToken(newToken);
      } catch { /* ignore parse errors while typing */ }
    }
  };

  const handlePayloadChange = (val: string) => {
    setPayloadText(val);
    if (decoded) {
      try {
        const newToken = rebuildToken(headerText, val, decoded.signatureRaw);
        setToken(newToken);
      } catch { /* ignore parse errors while typing */ }
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* blocked */ }
  };

  const expired = decoded ? isExpired(decoded.payload) : false;

  const sectionPaper = {
    p: 2,
    borderRadius: 2,
    flex: 1,
    minWidth: 0,
  };

  const monoField = {
    '& .MuiInputBase-root': {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '0.82rem',
    },
  };

  return (
    <Box>
      <PageHeader
        title="JWT Debugger"
        subtitle="JSON Web Tokens dekodieren, inspizieren und editieren"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'JWT Debugger' },
        ]}
      />

      {/* Token input */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Token</Typography>
            {decoded?.header && (
              <Chip
                label={String((decoded.header as Record<string, unknown>).alg || '?')}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
            {expired && (
              <Chip label="EXPIRED" size="small" color="error" sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }} />
            )}
            {decoded && !expired && decoded.payload && typeof decoded.payload.exp === 'number' && (
              <Chip label="VALID" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
            )}
          </Box>
          <Tooltip title={copied ? 'Kopiert!' : 'Token kopieren'}>
            <IconButton size="small" onClick={() => handleCopy(token)}>
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="JWT hier einfügen..."
          sx={monoField}
        />
        {!decoded && token.trim().length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Ungültiges JWT-Format. Ein JWT besteht aus drei Base64URL-Teilen, getrennt durch Punkte.
          </Alert>
        )}
      </Paper>

      {decoded && (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
          {/* Header */}
          <Paper sx={{ ...sectionPaper, borderTop: `3px solid ${theme.palette.info.main}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.info.main }}>
              Header
            </Typography>
            {decoded.headerError ? (
              <Alert severity="error" sx={{ mb: 1 }}>{decoded.headerError}</Alert>
            ) : (
              <TextField
                multiline
                minRows={5}
                maxRows={12}
                fullWidth
                value={headerText}
                onChange={(e) => handleHeaderChange(e.target.value)}
                sx={monoField}
              />
            )}
          </Paper>

          {/* Payload */}
          <Paper sx={{ ...sectionPaper, borderTop: `3px solid ${theme.palette.success.main}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.success.main }}>
              Payload
            </Typography>
            {decoded.payloadError ? (
              <Alert severity="error" sx={{ mb: 1 }}>{decoded.payloadError}</Alert>
            ) : (
              <>
                <TextField
                  multiline
                  minRows={5}
                  maxRows={12}
                  fullWidth
                  value={payloadText}
                  onChange={(e) => handlePayloadChange(e.target.value)}
                  sx={monoField}
                />
                {/* Claim info table */}
                {decoded.payload && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}>
                      Claims
                    </Typography>
                    {Object.entries(decoded.payload).map(([key, val]) => {
                      const ts = formatTimestamp(val);
                      return (
                        <Box
                          key={key}
                          sx={{
                            display: 'flex',
                            gap: 1,
                            py: 0.4,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            alignItems: 'baseline',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 60, color: theme.palette.primary.main }}
                          >
                            {key}
                          </Typography>
                          {CLAIM_INFO[key] && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: 100 }}>
                              {CLAIM_INFO[key]}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                            {ts && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, color: theme.palette.warning.main }}
                              >
                                ({ts})
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Paper>

          {/* Signature */}
          <Paper sx={{ ...sectionPaper, borderTop: `3px solid ${theme.palette.secondary.main}`, maxWidth: { lg: 300 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.secondary.main }}>
              Signatur
            </Typography>
            <Box
              sx={{
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                fontSize: '0.78rem',
                wordBreak: 'break-all',
                p: 1.5,
                borderRadius: 1,
                bgcolor: theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa',
                color: theme.palette.text.secondary,
              }}
            >
              {decoded.signatureRaw}
            </Box>
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
              Die Signatur kann im Browser nicht verifiziert werden, da der geheime Schlüssel serverseitig liegt.
              Editierte Tokens haben eine ungültige Signatur.
            </Alert>
          </Paper>
        </Box>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchJwtPage;
